import { useId, useState } from 'react';
import {
  formatIsoDate,
  type ImportResult,
  sendFinesImport,
  useImports,
  useInvalidateRecords,
} from '../api/records';
import { downloadCsv } from '../components/csv';
import { Icon } from '../components/Icon';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { usePageTitle } from '../layout/shellContext';

const TEMPLATE = {
  file: 'Шаблон нарушений.csv',
  header: ['Сотрудник', 'ID Bitrix24', 'Дата', 'Причина', 'Сумма'],
  example: ['Иванов Иван Иванович', '', '02.09.2026', 'Опоздание на смену', '500'],
};

const today = () => new Date().toISOString().slice(0, 10);
const shift = (date: string, days: number) => {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
};

const PREVIEW_COLUMNS = [
  { key: 'fullName', label: 'Сотрудник' },
  { key: 'occurredOn', label: 'Дата' },
  { key: 'reason', label: 'Причина' },
  { key: 'amountKopecks', label: 'Сумма, ₽', number: true },
];

function cell(key: string, value: unknown) {
  if (value === null || value === undefined) return '—';
  if (key === 'amountKopecks') return (Number(value) / 100).toLocaleString('ru-RU');
  if (key === 'occurredOn') return formatIsoDate(String(value));
  return String(value);
}

function FinesImport() {
  const inputId = useId();
  const [from, setFrom] = useState(shift(today(), -14));
  const [to, setTo] = useState(shift(today(), -1));
  const [paidOn, setPaidOn] = useState(today());
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const invalidate = useInvalidateRecords();

  const params = { paidOn, from, to };
  const run = async (commit: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const response = await sendFinesImport(params, file, commit);
      setResult(response);
      if (response.committed) await invalidate();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };
  const reset = () => {
    setResult(null);
    setError(null);
  };

  return (
    <section className="panel import-panel">
      <div className="panel-header">
        <h2>Нарушения за выплату</h2>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => downloadCsv(TEMPLATE.file, TEMPLATE.header, [TEMPLATE.example])}
        >
          <Icon name="download" size={14} />
          Шаблон
        </button>
      </div>

      <div className="import-fields">
        <label className="field">
          <span>Дата выплаты</span>
          <input
            className="input"
            type="date"
            value={paidOn}
            onChange={(event) => {
              setPaidOn(event.target.value);
              if (event.target.value) {
                setFrom(shift(event.target.value, -14));
                setTo(shift(event.target.value, -1));
              }
              reset();
            }}
          />
        </label>
        <label className="field">
          <span>За период с</span>
          <input
            className="input"
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              reset();
            }}
          />
        </label>
        <label className="field">
          <span>по</span>
          <input
            className="input"
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              reset();
            }}
          />
        </label>
      </div>

      <div className="import-file">
        <label htmlFor={inputId} className="btn-secondary">
          <Icon name="upload" size={14} />
          {file ? 'Другой файл' : 'Выбрать файл'}
        </label>
        <input
          id={inputId}
          className="visually-hidden"
          type="file"
          accept=".xlsx,.csv"
          aria-label="Файл нарушений"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            reset();
            event.target.value = '';
          }}
        />
        <span className="muted import-file-name">{file ? file.name : 'XLSX или CSV'}</span>
        <button
          type="button"
          className="btn-primary"
          disabled={!file || busy}
          onClick={() => void run(false)}
        >
          Проверить
        </button>
      </div>

      {error ? <ErrorState title="Файл не обработан" error={error} /> : null}

      {result ? (
        <div className="import-result">
          {result.committed ? (
            <p className="notice notice-ok">
              <Icon name="check" size={14} />
              Загружено: {result.rows} строк, сотрудников {result.employees}
              {result.replaces ? `, заменено прежних записей ${result.replaces}` : ''}.
            </p>
          ) : (
            <p className="import-summary">
              Строк без ошибок: <b>{result.rows}</b>, сотрудников: <b>{result.employees}</b>
              {result.replaces ? (
                <>
                  . Будет заменено прежних записей: <b>{result.replaces}</b>
                </>
              ) : null}
              {result.errorCount ? (
                <>
                  . Ошибок: <b className="text-bad">{result.errorCount}</b>
                </>
              ) : null}
            </p>
          )}

          {result.errors.length ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th className="number">Строка</th>
                    <th>Что исправить</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((item) => (
                    <tr key={`${item.row}-${item.message}`}>
                      <td className="number">{item.row}</td>
                      <td>{item.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {!result.committed && !result.errorCount && result.preview.length ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    {PREVIEW_COLUMNS.map((column) => (
                      <th key={column.key} className={column.number ? 'number' : undefined}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.preview.map((row) => (
                    <tr key={String(row.row)}>
                      {PREVIEW_COLUMNS.map((column) => (
                        <td key={column.key} className={column.number ? 'number' : undefined}>
                          {cell(column.key, row[column.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {!result.committed ? (
            <div className="panel-actions">
              <button type="button" className="btn-secondary" onClick={reset}>
                Отмена
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={busy || result.errorCount > 0}
                onClick={() => void run(true)}
              >
                {result.rows === 0 ? 'Загрузить выплату без нарушений' : 'Загрузить'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** Загрузка штрафов после выплаты: проверка, предпросмотр, загрузка, журнал. Только HR. */
export function ImportsPage() {
  usePageTitle('Загрузка нарушений');
  const imports = useImports();
  return (
    <div className="page">
      <div className="import-grid">
        <FinesImport />
      </div>
      <section className="panel">
        <div className="panel-header">
          <h2>Последние загрузки</h2>
        </div>
        {imports.isPending ? <LoadingState /> : null}
        {imports.isError ? <ErrorState error={imports.error} /> : null}
        {imports.data && !imports.data.items.length ? <EmptyState title="Файлы ещё не загружались" /> : null}
        {imports.data?.items.length ? (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Что</th>
                  <th>Файл</th>
                  <th className="number">Строк</th>
                  <th className="number">Заменено</th>
                  <th>Кто</th>
                </tr>
              </thead>
              <tbody>
                {imports.data.items.map((item) => (
                  <tr key={item.id}>
                    <td>{new Date(item.createdAt).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })}</td>
                    <td>
                      {item.kind === 'kpi'
                        ? `KPI ${formatIsoDate(item.periodFrom)} — ${formatIsoDate(item.periodTo)}`
                        : `Нарушения за выплату ${formatIsoDate(item.paidOn)}`}
                    </td>
                    <td>{item.fileName}</td>
                    <td className="number">{item.rows}</td>
                    <td className="number">{item.replaced}</td>
                    <td>{item.uploadedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}
